import { saveAs } from "file-saver";
import getImageSize from "image-size-from-url";

export interface ProductSpecData {
  name?: string;
  product_name?: string;
  type_name?: string;
  scope?: string;
  teaser?: string;
  teaser_composition?: string;
  composition?: string;
  promo_name?: string;
  trade_mark_name?: string;
  technology_name?: string;
  price_segment_name?: string;
  business_scopes?: any;
  attachments?: any[];
  label_attachments?: any[];
  specification_attachments?: any[];
}

export interface ProductSpecExportOptions {
  product: ProductSpecData;
  token: string;
  fileName?: string;
  requestPdfToken?: (
    contentBase64: string,
    fileName: string
  ) => Promise<string>;
  downloadExport?: (token: string, fileName: string) => void | Promise<void>;
}

type SpecExportVm = {
  token: string;
  $db: (operations: any[]) => Promise<any>;
  $q: (name: string) => any;
};

export function defaultDownloadExport(
  exportToken: string,
  fileName: string,
  authToken: string
) {
  const fileUrl = `${process.env.VUE_APP_UBIC_SERVER_ADDRESS}/api/v1/exports/${exportToken}?authorization=${authToken}`;
  saveAs(fileUrl, fileName);
}

export function createProductSpecExportOptions(
  vm: SpecExportVm,
  product: ProductSpecData,
  fileName?: string
): ProductSpecExportOptions {
  return {
    product,
    token: vm.token,
    fileName,
    requestPdfToken: async (contentBase64: string, docxFileName: string) => {
      console.debug("[FIX][productSpecExport] requestPdfToken", {
        docxFileName,
        contentLength: contentBase64.length
      });

      const result = await vm.$db([
        vm
          .$q("CONVERT_DOCX_TO_PDF")
          .raw("documents.convert_docx_to_pdf")
          .action({
            content_base64: contentBase64,
            file_name: docxFileName
          })
      ]);

      const pdfToken = result
        .getOperation("CONVERT_DOCX_TO_PDF")
        .getContent().token;

      console.debug("[FIX][productSpecExport] requestPdfToken success", {
        pdfToken
      });

      return pdfToken;
    },
    downloadExport: (exportToken: string, pdfFileName: string) => {
      console.debug("[FIX][productSpecExport] downloadExport", {
        exportToken,
        pdfFileName
      });
      defaultDownloadExport(exportToken, pdfFileName, vm.token);
    }
  };
}

function formatBusinessScopes(product: ProductSpecData): string {
  if (!product.business_scopes || !Array.isArray(product.business_scopes)) {
    return "";
  }

  return product.business_scopes
    .map((scope: any) => {
      if (typeof scope === "object" && scope !== null && scope.name) {
        return scope.name;
      }
      return String(scope);
    })
    .filter(Boolean)
    .join(", ");
}

export function buildTemplateData(product: ProductSpecData) {
  const data = {
    business_scopes: formatBusinessScopes(product),
    scope: product.scope || "",
    product_name: product.product_name || product.name || "",
    type_name: product.type_name || "",
    teaser_composition: product.teaser_composition || "",
    teaser: product.teaser || "",
    composition: product.composition || "",
    promo_name: product.promo_name || "",
    trade_mark_name: product.trade_mark_name || "",
    technology_name: product.technology_name || "",
    price_segment_name: product.price_segment_name || ""
  };

  console.debug("[productSpecExport.buildTemplateData]", {
    fieldKeys: Object.keys(data),
    hasAttachments: !!(
      (product.attachments && product.attachments.length) ||
      (product.label_attachments && product.label_attachments.length)
    )
  });

  return data;
}

function mergeAttachments(product: ProductSpecData): any[] {
  return [
    ...(product.attachments || []),
    ...(product.label_attachments || []),
    ...(product.specification_attachments || [])
  ];
}

async function loadImagesMap(
  attachments: any[] | undefined,
  token: string
): Promise<Record<string, any>> {
  const imagesMap: Record<string, any> = {};

  if (!attachments || !Array.isArray(attachments) || attachments.length === 0) {
    return imagesMap;
  }

  const serverAddress = process.env.VUE_APP_UBIC_SERVER_ADDRESS;
  let imageIndex = 0;

  for (const attachment of attachments) {
    if (!attachment || !attachment.id) {
      continue;
    }

    try {
      const imageUrl = `${serverAddress}/api/v1/files/download_preview/${attachment.id}?authorization=${token}`;
      const imageResponse = await fetch(imageUrl);
      if (!imageResponse.ok) {
        continue;
      }

      const contentType =
        imageResponse.headers.get("content-type") || "image/png";
      if (!contentType.startsWith("image/")) {
        console.debug("[productSpecExport] skip non-image attachment", {
          id: attachment.id,
          contentType
        });
        continue;
      }

      const imageArrayBuffer = await imageResponse.arrayBuffer();
      const imageBuffer = new Uint8Array(imageArrayBuffer);
      let extension = "png";

      if (contentType.includes("jpeg") || contentType.includes("jpg")) {
        extension = "jpeg";
      } else if (contentType.includes("png")) {
        extension = "png";
      } else if (contentType.includes("gif")) {
        extension = "gif";
      }

      const dimensions = await getImageSize(imageUrl);
      const width = 200;
      const height = (dimensions.height * width) / dimensions.width;

      imagesMap[`image${imageIndex}`] = {
        data: imageBuffer,
        extension,
        width,
        height
      };
      imageIndex += 1;
    } catch (imageError) {
      console.warn(
        `[productSpecExport] failed to load image ${attachment.id}`,
        imageError
      );
    }
  }

  return imagesMap;
}

function sanitizeFileName(name: string, extension: string) {
  return `${name}.${extension}`.replace(/[^\wа-яА-ЯёЁ\s.-]/gi, "_");
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Failed to read blob"));
        return;
      }
      const base64 = result.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export async function generateProductSpecDocxBlob(
  options: ProductSpecExportOptions
): Promise<Blob> {
  const { product, token } = options;
  const Docxtemplater = (await import("docxtemplater")).default;
  const PizZip = (await import("pizzip")).default;
  // @ts-ignore
  const ImageModule = (await import("docxtemplater-image-module-free")).default;

  if (!product) {
    throw new Error("Продукт не найден");
  }

  const templateResponse = await fetch("/files/template_v1.docx");
  if (!templateResponse.ok) {
    throw new Error("Не удалось загрузить шаблон");
  }

  const templateArrayBuffer = await templateResponse.arrayBuffer();
  const zip = new PizZip(templateArrayBuffer);
  const attachments = mergeAttachments(product);
  const imagesMap = await loadImagesMap(attachments, token);

  const getImage = (tagValue: any) => {
    if (
      typeof tagValue === "string" &&
      imagesMap[tagValue] &&
      imagesMap[tagValue].data
    ) {
      return imagesMap[tagValue].data;
    }
    console.warn(`[productSpecExport] image not found for tag: ${tagValue}`);
    return null;
  };

  const getSize = (_img: any, tagValue: any) => {
    if (
      typeof tagValue === "string" &&
      imagesMap[tagValue] &&
      imagesMap[tagValue].width &&
      imagesMap[tagValue].height
    ) {
      return [imagesMap[tagValue].width, imagesMap[tagValue].height];
    }
    return [400, 400];
  };

  const imageModule = new ImageModule({
    getImage,
    getSize,
    centered: false,
    fileType: "docx"
  });

  const doc = new Docxtemplater(zip, {
    modules: [imageModule],
    paragraphLoop: true,
    linebreaks: true
  });

  const data: any = buildTemplateData(product);
  Object.keys(imagesMap).forEach((key: string) => {
    if (imagesMap[key].data) {
      data[key] = key;
    }
  });

  console.debug("[productSpecExport]", {
    format: "docx",
    fieldKeys: Object.keys(data),
    imagesCount: Object.keys(imagesMap).length
  });

  doc.render(data);

  return doc.getZip().generate({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    compression: "DEFLATE"
  });
}

export async function exportProductSpecDocx(
  options: ProductSpecExportOptions
): Promise<void> {
  const { product } = options;
  const blob = await generateProductSpecDocxBlob(options);
  const fileName = sanitizeFileName(
    options.fileName || product.name || product.product_name || "spec",
    "docx"
  );
  saveAs(blob, fileName);
}

export async function exportProductSpecPdf(
  options: ProductSpecExportOptions
): Promise<void> {
  const { product, requestPdfToken, downloadExport } = options;

  if (!requestPdfToken || !downloadExport) {
    throw new Error("PDF export requires requestPdfToken and downloadExport");
  }

  const docxBlob = await generateProductSpecDocxBlob(options);
  const base64 = await blobToBase64(docxBlob);
  const docxName = sanitizeFileName(
    options.fileName || product.name || product.product_name || "spec",
    "docx"
  );

  console.debug("[productSpecExport.pdf]", {
    blobSize: docxBlob.size,
    fileName: docxName
  });

  const pdfToken = await requestPdfToken(base64, docxName);
  const pdfName = docxName.replace(/\.docx$/i, ".pdf");

  console.debug("[productSpecExport.pdf]", { convertToken: pdfToken });

  await downloadExport(pdfToken, pdfName);
}
